/**
 * 전사 자동 부여 룰 미리보기 — `role_attribute_grants` 전체.
 *
 * 운영 디버깅: "어떤 룰이 누구한테 권한을 주고 있나" 한눈에 + 룰별 매칭 사용자 수.
 * 룰 편집은 여기서 안 함 — 각 행 클릭 시 해당 그룹 편집 화면으로 이동.
 *
 * Phase 2.0 구현: 룰별 1+1 쿼리. 룰 수백 개 폭증 시 단일 집계로 최적화 (Phase 2.1).
 */
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

type Row = NonNullable<
  Awaited<ReturnType<typeof api.adminPermissionsAttributeRules>>
>['rules'][number];

export function AdminPermissionsRules() {
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'role' | 'attribute'>('role');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'permissions', 'attribute-rules'],
    queryFn: api.adminPermissionsAttributeRules,
  });

  const filtered = useMemo(() => {
    const all = data?.rules ?? [];
    if (!search.trim()) return all;
    const s = search.trim().toLowerCase();
    return all.filter(
      (r) =>
        r.roleName.toLowerCase().includes(s) ||
        r.attributeKey.toLowerCase().includes(s) ||
        r.attributeValue.toLowerCase().includes(s),
    );
  }, [data, search]);

  const totalRules = data?.rules.length ?? 0;
  const totalMatchCount = useMemo(
    () => (data?.rules ?? []).reduce((sum, r) => sum + r.matchCount, 0),
    [data],
  );

  return (
    <PageShell
      header={
        <PageHeader
          title="자동 부여 룰"
          count={totalRules}
          description={`전 그룹의 attribute 매칭 룰. 합계 ${totalMatchCount}건의 사용자-그룹 자동 부여가 잠재적으로 발생합니다 (exclusion 미반영, 실제 effective 와 다를 수 있음).`}
        />
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="그룹 / attribute / 값 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 flex-1 min-w-[200px] rounded-md border border-border bg-surface px-3 text-sm"
        />
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
        >
          <option value="role">그룹별</option>
          <option value="attribute">속성별 그룹</option>
          <option value="none">평면</option>
        </select>
      </div>

      {error instanceof ApiError ? (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error.message}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-border bg-surface-muted/40 px-3 py-6 text-center text-sm text-foreground-muted">
          {search.trim()
            ? '검색 결과 없음.'
            : '등록된 자동 부여 룰이 없습니다. 그룹 편집에서 "자동 부여 룰" 을 추가하세요.'}
        </div>
      ) : groupBy === 'none' ? (
        <RuleTable rows={filtered} />
      ) : (
        <GroupedRules rows={filtered} groupBy={groupBy} />
      )}
    </PageShell>
  );
}

function GroupedRules({ rows, groupBy }: { rows: Row[]; groupBy: 'role' | 'attribute' }) {
  const groups = useMemo(() => {
    const map = new Map<string, Row[]>();
    for (const r of rows) {
      const key = groupBy === 'role' ? r.roleName : r.attributeKey;
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows, groupBy]);

  return (
    <div className="space-y-4">
      {groups.map(([key, list]) => {
        const totalMatch = list.reduce((s, r) => s + r.matchCount, 0);
        const totalSkill = list[0]?.skillCount ?? 0; // role 그룹일 때만 의미. attribute 그룹은 0.
        return (
          <section key={key} className="rounded-md border border-border bg-surface">
            <header className="flex items-center justify-between border-b border-border px-3 py-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{key}</span>
                <span className="text-foreground-muted">
                  · 룰 {list.length}개 · 매칭 {totalMatch}건
                  {groupBy === 'role' ? ` · Skill ${totalSkill}개` : ''}
                </span>
              </div>
            </header>
            <RuleTable
              rows={list}
              hideRole={groupBy === 'role'}
              hideAttributeKey={groupBy === 'attribute'}
            />
          </section>
        );
      })}
    </div>
  );
}

function matchLabel(mode: 'exact' | 'prefix', negate: boolean): string {
  if (mode === 'exact') return negate ? 'NOT IN' : '=';
  return negate ? '하위 미포함' : '하위 포함';
}
function matchToneClass(mode: 'exact' | 'prefix', negate: boolean): string {
  if (mode === 'exact')
    return negate
      ? 'bg-destructive/15 text-destructive font-medium'
      : 'bg-surface-muted text-foreground font-medium';
  return negate ? 'bg-warning/15 text-warning font-medium' : 'bg-info/15 text-info font-medium';
}

function RuleTable({
  rows,
  hideRole,
  hideAttributeKey,
}: {
  rows: Row[];
  hideRole?: boolean;
  hideAttributeKey?: boolean;
}) {
  // 모든 group 의 컬럼 너비를 일관되게 유지하려고 table-fixed + colgroup 으로 명시.
  // 숨김 컬럼도 너비 유지 (cell 만 빈 값) — 그룹별 표가 독립 table 이라도 모두 동일 비율.
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col className="w-[22%]" /> {/* 그룹 */}
          <col className="w-[14%]" /> {/* 속성 */}
          <col className="w-[28%]" /> {/* 값 */}
          <col className="w-[12%]" /> {/* 매칭 */}
          <col className="w-[10%]" /> {/* 매칭 사용자 */}
          <col className="w-[7%]" /> {/* Skill */}
          <col className="w-[7%]" /> {/* 편집 */}
        </colgroup>
        <thead className="bg-surface-muted text-foreground-muted">
          <tr>
            <th className="px-3 py-2 text-left font-medium">그룹</th>
            <th className="px-3 py-2 text-left font-medium">속성</th>
            <th className="px-3 py-2 text-left font-medium">값</th>
            <th className="px-3 py-2 text-left font-medium">매칭</th>
            <th className="px-3 py-2 text-right font-medium">매칭 사용자</th>
            <th className="px-3 py-2 text-right font-medium">Skill</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={`${r.roleId}-${r.attributeKey}-${r.attributeValue}-${r.matchMode}-${i}`}
              className="border-t border-border hover:bg-surface-muted/40"
            >
              <td className="px-3 py-2 truncate">
                {hideRole ? (
                  <span className="text-foreground-muted">↪</span>
                ) : (
                  <>
                    <span className="font-medium" title={r.roleName}>
                      {r.roleName}
                    </span>
                    {!r.roleEnabled ? (
                      <span className="ml-1 text-xs text-foreground-muted">(비활성)</span>
                    ) : null}
                  </>
                )}
              </td>
              <td className="px-3 py-2 truncate font-mono text-xs">
                {hideAttributeKey ? (
                  <span className="text-foreground-muted">↪</span>
                ) : (
                  <span title={r.attributeKey}>{r.attributeKey}</span>
                )}
              </td>
              <td className="px-3 py-2 truncate" title={r.attributeValue}>
                {r.attributeValue}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`rounded px-1.5 py-0.5 text-xs ${matchToneClass(r.matchMode, r.negate)}`}
                >
                  {matchLabel(r.matchMode, r.negate)}
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {r.matchCount}
                {!r.roleEnabled && r.matchCount > 0 ? (
                  <span
                    className="ml-1 text-xs text-foreground-muted"
                    title="비활성 그룹 — 실제 부여 안 됨"
                  >
                    ⚠
                  </span>
                ) : null}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{r.skillCount}</td>
              <td className="px-3 py-2 text-right">
                <Link
                  to="/admin/roles"
                  className="text-xs text-foreground-muted hover:text-foreground hover:underline"
                  title="그룹 편집"
                >
                  편집
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
